import { useTranslate } from '../../application/i18n/LocaleProvider';
import { carName } from '../../domain/cars/carCatalogue';
import { heroTake } from '../../domain/cars/carSchema';
import { gallerySources } from './carArt';

/**
 * The other takes — every picture of this car except the one the card above is
 * already showing.
 *
 * THE DELIVERY IS UNEVEN AND ALWAYS WILL BE, and the second art drop made it
 * more so rather than less. The F40 arrived as ten pictures — side, front, three
 * quarters from both ends, the rear, the cabin, the engine, a wheel, a headlight
 * — where thirteen of the twenty-four came with three to five and ten came with
 * a single side profile. So this band draws nine frames for one car, two to four
 * for half of them, and nothing at all for the rest; none of those is a special
 * case, because `gallerySources` asks the car what it has.
 *
 * The behaviour that matters most is the one nobody sees. A car whose only take
 * is its hero renders NOTHING — no heading, no empty state, no placeholder and
 * no "more pictures coming" line. That is what lets a picture arrive late, or
 * not at all, without anybody having to remember to guard a band.
 *
 * A scroll-snapping rail rather than a grid: these are wide pictures of one
 * object, and a grid of nine thumbnails of the same car reads as a contact
 * sheet.
 * The rail is keyboard-scrollable, which is why there are no arrow buttons on it
 * — the mechanism IS the control.
 *
 * Every picture is named, and this is the one place in the product where the
 * artwork carries a real `alt`: the take is the information here. On a card the
 * name is already beside the picture, so the same photograph is decorative.
 *
 * EVERY FRAME IS A WAY IN. It stays the band it was — the takes the card is not
 * showing, at 78% of the screen in a 4:3 crop — and tapping one opens that take
 * whole in the full-screen viewer, which is the only place all of a car's
 * pictures are held (SPEC-012 D-15). A button around the frame's contents rather
 * than a handler on the `<img>`, so it is reachable by keyboard and says what it
 * does; the band is outside `.car-deck`, so nothing here touches the deck's
 * sideways gesture.
 */
export default function CarGallery({ car, onOpenTake }) {
    const t = useTranslate();
    // The take the card above is showing, so the rail never opens with the
    // picture the reader is already looking at. That is `heroTake` again since
    // the card went back to the side profile — the two have to be asked the same
    // question, or the card's own picture turns up as the first thumbnail under
    // it.
    const sources = gallerySources(car, heroTake(car));

    if (sources.length === 0) {
        return null;
    }

    const name = carName(car);

    return (
        <section className="car-gallery" aria-labelledby="car-gallery-title">
            <header className="section-heading">
                <h2 className="section-heading__text" id="car-gallery-title">{t('cars.section.gallery')}</h2>
                <span className="section-heading__rule" aria-hidden="true" />
            </header>

            <ul className="car-gallery__rail" aria-label={t('a11y.gallery', { car: name })}>
                {sources.map((source) => (
                    <li className="car-gallery__frame" key={source.take}>
                        <button
                            type="button"
                            className="car-gallery__open"
                            onClick={() => onOpenTake?.(source.take)}
                            /* Labelled by what activating it DOES. Without this
                               the name is the picture's `alt` followed by the
                               caption, which says the take twice and never says
                               that pressing it opens anything. */
                            aria-label={t('cars.viewer.openTake', { take: t(`cars.take.${source.take}`) })}
                        >
                            <img
                                className="car-gallery__art"
                                src={source.src}
                                srcSet={source.srcSet}
                                sizes="(min-width: 640px) 420px, 78vw"
                                alt={t('a11y.galleryImage', { car: name, take: t(`cars.take.${source.take}`) })}
                                /* Below the fold on every phone, always: this
                                   band is beneath the card, the specification
                                   and the prose. Eight extra pictures fetched
                                   eagerly would be eight requests competing with
                                   the hero. */
                                loading="lazy"
                                decoding="async"
                            />
                            {/* The take, printed. The `alt` says the same thing
                                for somebody who cannot see the picture; this is
                                for somebody who can see it and still cannot tell
                                a top-front from a top-front2 at a glance. */}
                            <p className="car-gallery__caption">{t(`cars.take.${source.take}`)}</p>
                        </button>
                    </li>
                ))}
            </ul>
        </section>
    );
}
